import { Typography, useTheme } from '@mui/material';
import { PolicyRule } from '../../../../model/interfaces/policyRule';
import React, { useRef } from 'react';
import { TitledPaperWithSaveBar } from '../../../../modules/components/titledPaper/titledPaperWithSaveBar/titledPaperWithSaveBar';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Formik, FormikProps } from 'formik';
import { ScoreModel } from '../../../../model/interfaces/scoreModel';
import { useScoreModel } from '../../../../model/context/scoreModelContext';
import { useScoreModel2PolicyRule } from '../../../../model/useScoreModel2PolicyRule';
import { ScoreModelEditorActiveRules } from './scoreModelEditorActiveRules';
import { ScoreModelEditorNameField } from './scoreModelEditorNameField';
import { useActionModal } from '../../../../modules/actionModal';
import { useSnackbar } from 'notistack';
import { customTheme } from '../../../../styles/defaultTheme';

interface ScoreModelEditorProps {
  scoreModel: ScoreModel;
}

export const ScoreModelEditor = (props: ScoreModelEditorProps) => {
  const { scoreModel } = props;
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<FormikProps<ScoreModel> | null>(null);

  const { openActionModal } = useActionModal();
  const { createScoreModel, updateScoreModel, deleteScoreModel, switchScoreModel, refresh } =
    useScoreModel();
  const { createScoreModel2PolicyRules, deleteScoreModel2PolicyRules } = useScoreModel2PolicyRule();

  const onDelete = async () => {
    try {
      await deleteScoreModel();
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.scoremodel.delete.success',
          defaultMessage: 'Score model deleted'
        }),
        { variant: 'success' }
      );
      switchScoreModel(undefined);
    } catch (e) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.scoremodel.delete.error',
          defaultMessage: 'Could not delete score model'
        }),
        { variant: 'error' }
      );
    }
  };

  const onSaveNewScoreModel = async (
    values: ScoreModel,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      const newScoreModel: ScoreModel = await createScoreModel({ ...values, scoreModelId: 0 });
      switchScoreModel(newScoreModel.scoreModelId);
      await createScoreModel2PolicyRules(newScoreModel, values.policyRules);
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.scoremodel.create.success',
          defaultMessage: 'New score model created'
        }),
        { variant: 'success' }
      );
      setSubmitting(false);
      refresh();
    } catch (e) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.scoremodel.create.error',
          defaultMessage: 'Could not create a new score model'
        }),
        { variant: 'error' }
      );
    }
  };

  const onSave = async (values: ScoreModel, setSubmitting: (isSubmitting: boolean) => void) => {
    const oldRules: PolicyRule[] = scoreModel.policyRules;
    const newlyAddedRules = values.policyRules.filter((item) => !oldRules.includes(item));
    const removedRules = oldRules.filter((item) => !values.policyRules.includes(item));
    try {
      await updateScoreModel(values.name);
      await createScoreModel2PolicyRules(values, newlyAddedRules);
      await deleteScoreModel2PolicyRules(scoreModel, removedRules);
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.scoremodel.update.success',
          defaultMessage: 'Score model updated'
        }),
        { variant: 'success' }
      );
      refresh();
    } catch (e) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.scoremodel.update.error',
          defaultMessage: 'Could not update score model'
        }),
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const requestDelete = () => {
    openActionModal(
      <FormattedMessage
        id="actionModal.scoreModel.delete.header"
        defaultMessage="Delete {scoremodel}?"
        values={{ scoremodel: scoreModel.name }}
      />,
      <FormattedMessage
        id="actionModal.scoreModel.delete.content"
        defaultMessage="Are you sure you want to delete {scoremodel}?"
        values={{ scoremodel: scoreModel.name }}
      />,
      'Delete',
      'Cancel',
      () => onDelete()
    );
  };

  return (
    <Formik
      initialValues={scoreModel}
      onSubmit={(values, { setSubmitting }) => {
        //Check whether scoreModel is newly created or not
        Number.isNaN(values.scoreModelId)
          ? onSaveNewScoreModel(values, setSubmitting)
          : onSave(values, setSubmitting);
      }}
      innerRef={formRef}
      enableReinitialize>
      {({ values }) => (
        <TitledPaperWithSaveBar
          color={customTheme.palette.background.default}
          title={intl.formatMessage({
            id: 'ScoreModel'
          })}
          onClickDelete={requestDelete}
          changeDate={scoreModel.changedDate}>
          <Form style={{ display: 'contents' }}>
            <ScoreModelEditorNameField />
            {values && values.policyRules ? (
              <ScoreModelEditorActiveRules values={values} formRef={formRef} />
            ) : (
              <Typography>Loading...</Typography>
            )}
          </Form>
        </TitledPaperWithSaveBar>
      )}
    </Formik>
  );
};
